import { css } from 'aphrodite';
import * as React from 'react';
import type { IAccount } from '../../../extViewmodels';
import * as Api from '../../../extViewmodels';
import { useDebounceValue } from '../../../hooks/useDebounceValue';
import { useAdminAccountFilterQuery } from '../../../queries/Admin/Account/useAdminAccountFilterQuery';
import { AccountFilterCriteriaProperty, type IAccountsFilterRequest } from '../../../viewmodels/AdminViewModels';
import { DataGridColumn, DataGridColumns } from '../../../web/components/DataGrid';
import { ChevronDown } from '../../../web/components/svgs/icons/ChevronDown';
import { Button } from '../../../web/components-v2/Button';
import { Checkbox } from '../../../web/components-v2/Checkbox';
import {
	Dropdown,
	DropdownContent,
	DropdownRadioGroup,
	DropdownRadioItem,
	DropdownTrigger,
} from '../../../web/components-v2/Dropdown';
import {
	Combobox,
	ComboboxContent,
	ComboboxInput,
	ComboboxList,
	ComboboxStatus,
} from '../../../web/components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemTitle,
} from '../../../web/components-v2/Combobox/ComboboxAvatarItem';
import { Input } from '../../../web/components-v2/Input';
import { Popover, PopoverContent, PopoverTrigger } from '../../../web/components-v2/Popover';
import {
	getFilterOptionKey,
	type InboxFilterOption,
	type InboxFilters,
	PRIORITY_FILTER_OPTIONS,
	STATUS_FILTER_OPTIONS,
} from './inboxFilters';
import { ticketTableStyles as ts } from './styles/ticketTableStyles';
import type { TicketTableColumnKey } from './ticketTableColumns';

/**
 * A header cell's trigger text. Once a filter is on, its value takes the whole cell and the column's
 * own name steps aside: a heading plus a person's name inside a budgeted column leaves neither of
 * them readable, and the column a value sits over is the one thing the rows beneath already make
 * obvious. The name stays on the control's accessible label.
 */
function HeaderFilterLabel({ label, value }: { label: string; value?: string }) {
	return value ? <span className={css(ts.headerBtnValue)}>{value}</span> : <>{label}</>;
}

interface HeaderFilterProps<T> {
	label: string;
	options: ReadonlyArray<InboxFilterOption<T>>;
	value: T;
	inactiveValue: T;
	onSelect: (value: T) => void;
}

function HeaderFilter<T>({ label, options, value, inactiveValue, onSelect }: HeaderFilterProps<T>) {
	const active = !Object.is(value, inactiveValue);
	const selectedKey = getFilterOptionKey(options, value);
	const valueLabel = options.find(option => Object.is(option.value, value))?.label ?? 'Unknown';

	return (
		<Dropdown>
			<DropdownTrigger
				render={
					<Button
						type='button'
						variant='ghost'
						styles={active ? [ts.headerBtn, ts.headerBtnActive] : [ts.headerBtn]}
						title={`Filter by ${label.toLowerCase()}`}
						aria-label={active ? `${label}: ${valueLabel}` : undefined}
					/>
				}
			>
				<HeaderFilterLabel label={label} value={active ? valueLabel : undefined} />
				<ChevronDown className={css(ts.headerBtnChevron)} width={12} height={12} />
			</DropdownTrigger>
			<DropdownContent align='start' styles={[ts.filterMenu]}>
				<DropdownRadioGroup
					value={selectedKey}
					onValueChange={key => {
						const nextOption = options.find(option => option.key === key);
						if (nextOption) onSelect(nextOption.value);
					}}
				>
					{options.map(option => (
						<DropdownRadioItem key={option.key} value={option.key} styles={[ts.filterMenuItem]}>
							{option.label}
							{option.hint !== undefined && (
								<span className={css(ts.menuItemHint)} title={option.hintTitle}>
									{option.hint}
								</span>
							)}
						</DropdownRadioItem>
					))}
				</DropdownRadioGroup>
			</DropdownContent>
		</Dropdown>
	);
}

interface ContactFilterPanelProps {
	initialValue: string;
	onCommit: (next: string | undefined) => void;
	onClose: () => void;
}

/**
 * Draft input for the Contact header filter. The draft stays local and commits upward debounced so
 * the grid filters while the user types without refetching (and clearing row selection) on every
 * keystroke. Closing the popover flushes the draft, so a quick type-then-dismiss still filters.
 */
function ContactFilterPanel({ initialValue, onCommit, onClose }: ContactFilterPanelProps) {
	const [input, setInput] = React.useState(initialValue);
	const debounced = useDebounceValue(input.trim(), 300);

	const stateRef = React.useRef({ input, committed: initialValue.trim(), onCommit });
	stateRef.current.input = input;
	stateRef.current.onCommit = onCommit;

	const commit = React.useCallback((next: string) => {
		const state = stateRef.current;
		if (next === state.committed) return;
		state.committed = next;
		state.onCommit(next || undefined);
	}, []);

	React.useEffect(() => {
		commit(debounced);
	}, [commit, debounced]);
	React.useEffect(() => () => commit(stateRef.current.input.trim()), [commit]);

	return (
		<div className={css(ts.contactFilterPanel)}>
			<Input
				value={input}
				placeholder='Name or email'
				aria-label='Filter by contact name or email'
				autoFocus
				onChange={e => setInput(e.target.value)}
				onKeyDown={e => {
					if (e.key === 'Enter') {
						e.preventDefault();
						commit(input.trim());
						onClose();
					}
				}}
			/>
			{input.trim() !== '' && (
				<Button
					type='button'
					variant='ghost'
					styles={[ts.contactFilterClearBtn]}
					onClick={() => {
						commit('');
						onClose();
					}}
				>
					Clear contact filter
				</Button>
			)}
		</div>
	);
}

interface HeaderContactFilterProps {
	value: string | undefined;
	onCommit: (next: string | undefined) => void;
}

function HeaderContactFilter({ value, onCommit }: HeaderContactFilterProps) {
	const [open, setOpen] = React.useState(false);
	const active = !!value;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type='button'
					variant='ghost'
					styles={active ? [ts.headerBtn, ts.headerBtnActive] : [ts.headerBtn]}
					title='Filter by contact'
					aria-label={active ? `Contact: ${value}` : undefined}
				>
					<HeaderFilterLabel label='Contact' value={value} />
					<ChevronDown className={css(ts.headerBtnChevron)} width={12} height={12} />
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' styles={[ts.contactFilterPopover]}>
				<ContactFilterPanel initialValue={value ?? ''} onCommit={onCommit} onClose={() => setOpen(false)} />
			</PopoverContent>
		</Popover>
	);
}

interface AccountFilterValue {
	id: string;
	name?: string;
}

interface AccountFilterPanelProps {
	initialAccount?: AccountFilterValue;
	onCommit: (next: AccountFilterValue | undefined) => void;
	onClose: () => void;
}

/**
 * Account filters need an account id rather than a text fragment. Search the admin account index
 * by name or id, then commit the chosen account's id to the ticket query.
 */
function AccountFilterPanel({ initialAccount, onCommit, onClose }: AccountFilterPanelProps) {
	const [searchValue, setSearchValue] = React.useState(initialAccount?.name ?? initialAccount?.id ?? '');
	const debouncedSearchValue = useDebounceValue(searchValue.trim(), 250);
	const accountFilter = React.useMemo<IAccountsFilterRequest>(() => {
		const criteria: Api.IFilterCriteria<AccountFilterCriteriaProperty>[] = [];
		if (debouncedSearchValue) {
			criteria.push({
				op: Api.FilterOperator.Or,
				criteria: [
					{ property: AccountFilterCriteriaProperty.Name, value: debouncedSearchValue },
					{ property: AccountFilterCriteriaProperty.Id, value: debouncedSearchValue },
				],
			});
		}
		return { criteria, op: Api.FilterOperator.And };
	}, [debouncedSearchValue]);
	const accountsQuery = useAdminAccountFilterQuery({
		filter: accountFilter,
		autocompleteFragment: debouncedSearchValue || undefined,
		pageSize: 20,
		enabled: debouncedSearchValue.length > 0,
	});
	const accounts = React.useMemo(
		() => accountsQuery.data?.pages.flatMap(page => page.values ?? []) ?? [],
		[accountsQuery.data]
	);
	const selectedAccount = accounts.find(account => account.id === initialAccount?.id) ?? null;

	return (
		<div className={css(ts.contactFilterPanel)}>
			<Combobox
				items={accounts}
				value={selectedAccount}
				onValueChange={(account: IAccount | null) => {
					if (!account?.id) {
						onCommit(undefined);
						setSearchValue('');
						return;
					}
					onCommit({ id: account.id, name: account.companyName });
					setSearchValue(account.companyName ?? account.id);
					onClose();
				}}
				inputValue={searchValue}
				onInputValueChange={setSearchValue}
				itemToStringLabel={(account: IAccount) => `${account.companyName ?? ''} ${account.id}`}
				isItemEqualToValue={(account: IAccount, value: IAccount) => account.id === value.id}
			>
				<ComboboxInput aria-label='Search accounts' placeholder='Search accounts...' showClear={!!initialAccount} />
				<ComboboxContent styles={[ts.contactFilterPopover]}>
					<ComboboxStatus>
						{accountsQuery.isPending || accountsQuery.isFetching
							? 'Searching...'
							: accounts.length === 0 && debouncedSearchValue
								? `No accounts found for "${debouncedSearchValue}"`
								: 'Search accounts by name or id'}
					</ComboboxStatus>
					<ComboboxList>
						{(account: IAccount) => (
							<ComboboxAvatarItem key={account.id} value={account}>
								<ComboboxAvatarItemAvatar
									imageUrl=''
									fallbackText={(account.companyName || account.id || 'AC').substring(0, 2).toUpperCase()}
								/>
								<ComboboxAvatarItemTitle>{account.companyName || account.id}</ComboboxAvatarItemTitle>
							</ComboboxAvatarItem>
						)}
					</ComboboxList>
				</ComboboxContent>
			</Combobox>
			{initialAccount && (
				<Button
					type='button'
					variant='ghost'
					styles={[ts.contactFilterClearBtn]}
					onClick={() => {
						onCommit(undefined);
						onClose();
					}}
				>
					Clear account filter
				</Button>
			)}
		</div>
	);
}

interface HeaderAccountFilterProps {
	accountId: string | undefined;
	accountName: string | undefined;
	onCommit: (next: AccountFilterValue | undefined) => void;
}

function HeaderAccountFilter({ accountId, accountName, onCommit }: HeaderAccountFilterProps) {
	const [open, setOpen] = React.useState(false);
	const active = !!accountId;
	const label = accountName || accountId;

	return (
		<Popover open={open} onOpenChange={setOpen}>
			<PopoverTrigger asChild>
				<Button
					type='button'
					variant='ghost'
					styles={active ? [ts.headerBtn, ts.headerBtnActive] : [ts.headerBtn]}
					title='Filter by account'
					aria-label={active ? `Account: ${label}` : undefined}
				>
					<HeaderFilterLabel label='Account' value={active ? label : undefined} />
					<ChevronDown className={css(ts.headerBtnChevron)} width={12} height={12} />
				</Button>
			</PopoverTrigger>
			<PopoverContent align='start' styles={[ts.contactFilterPopover]}>
				<AccountFilterPanel
					initialAccount={accountId ? { id: accountId, name: accountName } : undefined}
					onCommit={onCommit}
					onClose={() => setOpen(false)}
				/>
			</PopoverContent>
		</Popover>
	);
}

interface TicketTableHeaderProps {
	columns: readonly TicketTableColumnKey[];
	allSelected: boolean;
	hasSelection: boolean;
	onToggleAll: () => void;
	filters: InboxFilters;
	onFiltersChange: (next: InboxFilters) => void;
	categoryOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	assigneeOptions: ReadonlyArray<InboxFilterOption<string | undefined>>;
	isSearching: boolean;
}

export const TicketTableHeader: React.FC<TicketTableHeaderProps> = ({
	columns,
	allSelected,
	hasSelection,
	onToggleAll,
	filters,
	onFiltersChange,
	categoryOptions,
	assigneeOptions,
	isSearching,
}) => {
	const dateSortOldest = filters.sort === 'oldest';
	const dateSortActive = filters.sort === 'newest' || dateSortOldest;
	const toggleDateSort = () => onFiltersChange({ ...filters, sort: filters.sort === 'newest' ? 'oldest' : 'newest' });

	const renderColumn = (column: TicketTableColumnKey) => {
		switch (column) {
			case 'select':
				return (
					<DataGridColumn key={column} styles={[ts.cellCheckbox]}>
						<Checkbox
							checked={allSelected ? true : hasSelection ? 'indeterminate' : false}
							onCheckedChange={onToggleAll}
							aria-label='Select all tickets on this page'
						/>
					</DataGridColumn>
				);
			case 'number':
				return (
					<DataGridColumn key={column} styles={[ts.numberHeader]}>
						#
					</DataGridColumn>
				);
			case 'subject':
				return <DataGridColumn key={column}>Subject</DataGridColumn>;
			case 'contact':
				return (
					<DataGridColumn key={column}>
						{isSearching ? (
							'Contact'
						) : (
							<HeaderContactFilter
								value={filters.contact}
								onCommit={contact => onFiltersChange({ ...filters, contact })}
							/>
						)}
					</DataGridColumn>
				);
			case 'account':
				return (
					<DataGridColumn key={column}>
						<HeaderAccountFilter
							accountId={filters.accountId}
							accountName={filters.accountName}
							onCommit={account => onFiltersChange({ ...filters, accountId: account?.id, accountName: account?.name })}
						/>
					</DataGridColumn>
				);
			case 'status':
				return (
					<DataGridColumn key={column}>
						<HeaderFilter
							label='Status'
							options={STATUS_FILTER_OPTIONS}
							value={filters.status}
							inactiveValue={undefined}
							onSelect={status => onFiltersChange({ ...filters, status })}
						/>
					</DataGridColumn>
				);
			case 'priority':
				return (
					<DataGridColumn key={column}>
						<HeaderFilter
							label='Priority'
							options={PRIORITY_FILTER_OPTIONS}
							value={filters.priority}
							inactiveValue={undefined}
							onSelect={priority => onFiltersChange({ ...filters, priority })}
						/>
					</DataGridColumn>
				);
			case 'category':
				return (
					<DataGridColumn key={column}>
						<HeaderFilter
							label='Category'
							options={categoryOptions}
							value={filters.categoryId}
							inactiveValue={undefined}
							onSelect={categoryId => onFiltersChange({ ...filters, categoryId })}
						/>
					</DataGridColumn>
				);
			case 'assignee':
				return (
					<DataGridColumn key={column}>
						<HeaderFilter
							label='Assignee'
							options={assigneeOptions}
							value={filters.assigneeId}
							inactiveValue={undefined}
							onSelect={assigneeId => onFiltersChange({ ...filters, assigneeId })}
						/>
					</DataGridColumn>
				);
			case 'lastUpdate':
				return (
					<DataGridColumn
						key={column}
						aria-sort={isSearching || !dateSortActive ? undefined : dateSortOldest ? 'ascending' : 'descending'}
					>
						{isSearching ? (
							'Last update'
						) : (
							<Button
								type='button'
								variant='ghost'
								styles={[ts.headerBtn]}
								onClick={toggleDateSort}
								title='Toggle date order'
							>
								Last update
								{dateSortActive && (
									<ChevronDown className={css(dateSortOldest && ts.headerChevronAsc)} width={12} height={12} />
								)}
							</Button>
						)}
					</DataGridColumn>
				);
			default:
				throw new Error(`Unsupported ticket table column: ${column}`);
		}
	};

	return <DataGridColumns>{columns.map(renderColumn)}</DataGridColumns>;
};
